// ANGULAR
import { Injectable } from '@angular/core';

// NGRX
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';

// RXJS
import { Observable, of, throwError } from 'rxjs';
import { catchError, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';

// EXTERNAL DEPENDENCY
import { get } from 'lodash';

// SERVICES
import { RequestService } from '../services/request.service';

// ACTIONS AND STATES
import * as globalActions from '../../../global/store/actions';
import { State } from '../../../root-state';
import * as RequestActions from './actions';

// COMMON INTERFACES AND ENUMS
import { ModalType } from '../../../../models/common/modal/modal-config';

// CONSTANT
import { REQUEST_NGRX_CONSTANTS } from '../../../../constants/ngrx-store/request';


/**
 * This effect is for listen Request actions, execute Request service call and dispatch in request reducer.
 * For Request api call purpose.
 *
 * @author Nirmal Jodhani <nirmal.jodhani@saeculumsolutions.com>
 *
 * Notes:-
 * Date: 17/06/2020 (Nirmal Jodhani <nirmal.jodhani@saeculumsolutions.com>)  created request effect
 */
@Injectable()
export class RequestEffects {
  constructor(
    private actions$: Actions,
    private store: Store<State>,
    private requestService: RequestService) { }

  /**
   * @param type : Request action name.
		  * Request Effect
		  * @param payload : object to identify common Request method base on module and method.
		  * @returns Empty action dispatch to reducer.
		  */
  RequestCommonEffect$ = createEffect(() => this.actions$.pipe(
    ofType<RequestActions.RequestCommonAction>(RequestActions.ActionTypes.REQUEST_COMMON),
    withLatestFrom(this.store),
    switchMap(([action, state]): Observable<any> => {
      // get common constant base action module and method.
      // changed to payload type from payload.data.method
      const common = REQUEST_NGRX_CONSTANTS[action.payload.module][action.payload.method];

      // avoid service call if data already exists in store.
      if (state[common.STATE]) {
        return of(new globalActions.EmptyAction({}));
      }
      // Only sending payload data

      return this.requestService[common.API_METHOD](action.payload.data)
        .pipe(

          mergeMap((data: any) => {
            // throwing an error on if the status is not 200
            if (data.status !== 200) { return throwError(() => data); }
            let actions: any[] = [];


            // dispatch Request success action
            actions = [new RequestActions.RequestCommonSuccessAction({ data, common })];

            if (common.SUCCESS_NOTIFICATION) {
              // dispatch popup notification for open success popup
              actions.push(new globalActions.PopupNotificationAction({
                actionType: RequestActions.ActionTypes.REQUEST_COMMON, type: ModalType.SUCCESS,
                callback: action.payload.callback, title: common.TITLE, message: common.SUCCESS,
                code: `${common.SUCCESS}`, response: get(data, common.API_RESPONSE),
              }));
            } else {
              action.payload.callback({ success: true, data: get(data, common.API_RESPONSE) });
            }

            return actions;
          }),
          catchError(error =>
            // dispatch popup notification for open error popup
            of(new globalActions.PopupNotificationAction({
              actionType: RequestActions.ActionTypes.REQUEST_COMMON, type: ModalType.ERROR,
              message: error && error.error && error.error.message, callback: action.payload.callback, error: (error || error.error),
              // code: `ERROR.${error.code || error.error.code}`,
              title: common.TITLE,
            }))));
    }),
  ));
}
